import { Controller } from "@hotwired/stimulus"

/**
 * Unsaved Changes Controller
 *
 * Tracks the dirty state of a form and reveals the "unsaved changes"
 * indicator in the fixed action bar. No navigation blocking.
 *
 * Usage:
 * <form data-controller="unsaved-changes"
 *       data-action="input->unsaved-changes#markDirty submit->unsaved-changes#markClean">
 *   ...
 *   <div data-unsaved-changes-target="indicator" class="hidden">Modifications non enregistrées</div>
 * </form>
 */
export default class extends Controller {
  static targets = ["indicator"]

  connect() {
    this.isDirty = false
    // restore the scroll position saved when the form was submitted from this page.
    // Turbo resets the scroll after rendering the visit (even after turbo:load),
    // so pin the position during a few frames until it sticks.
    const saved = sessionStorage.getItem(this.scrollKey)
    if (saved !== null) {
      sessionStorage.removeItem(this.scrollKey)
      const top = parseInt(saved, 10)
      let frames = 15
      const pin = () => {
        window.scrollTo({ top: top, behavior: "instant" })
        if (frames-- > 0) requestAnimationFrame(pin)
      }
      pin()
    }
  }

  get scrollKey() {
    return `formScroll:${location.pathname}`
  }

  markDirty() {
    this.isDirty = true
    if (this.hasIndicatorTarget) {
      this.indicatorTarget.classList.remove("hidden")
      this.indicatorTarget.classList.add("flex")
    }
    // a stale flash message no longer reflects the form state
    this.element.querySelectorAll(".action-bar-flash").forEach(el => el.classList.add("hidden"))
  }

  markClean(event) {
    this.isDirty = false
    if (this.hasIndicatorTarget) {
      this.indicatorTarget.classList.add("hidden")
      this.indicatorTarget.classList.remove("flex")
    }
    // saving from within the page: come back to the same scroll position
    if (event?.type === "submit") {
      sessionStorage.setItem(this.scrollKey, window.scrollY)
    }
  }
}
